import { Injectable, inject, signal, computed } from '@angular/core';
import { SailsService } from './sails.service';
import { Router } from '@angular/router';

export interface Notificacion {
  id: number;
  id_usuario: number;
  id_usuario_origen: number | null;
  tipo: string;
  titulo: string;
  mensaje: string;
  id_envio: number | null;
  url_destino: string | null;
  leida: boolean;
  createdAt: number;
  updatedAt: number;
}

@Injectable({ providedIn: 'root' })
export class NotificacionesService {
  private sailsService = inject(SailsService);
  private router = inject(Router);

  // ── Signals de estado ────────────────────────────────────────────────────────

  readonly notificaciones = signal<Notificacion[]>([]);
  readonly notificacionesEquipo = signal<Notificacion[]>([]);
  readonly cargando = signal(false);

  private socketSubscribed = false;

  readonly noLeidas = computed(() => this.notificaciones().filter((n) => !n.leida).length);

  readonly noLeidasEquipo = computed(
    () => this.notificacionesEquipo().filter((n) => !n.leida).length
  );

  // ── Inicialización ───────────────────────────────────────────────────────────

  /** Carga inicial y suscripción al socket.
   *  Llamar una vez desde el AppComponent o barra-superior al conectar. */
  init(): void {
    this.cargar();
    if (!this.socketSubscribed) {
      this.socketSubscribed = true;
      this.suscribirSocket();
    }
  }

  // ── API calls ────────────────────────────────────────────────────────────────

  cargar(): void {
    this.cargando.set(true);
    this.sailsService.get('/notificaciones/listar').subscribe({
      next: (res) => {
        this.notificaciones.set(res.data.notificaciones ?? []);
        this.notificacionesEquipo.set(res.data.notificacionesEquipo ?? []);
        this.cargando.set(false);
      },
      error: () => this.cargando.set(false),
    });
  }

  marcarLeida(id: number): void {
    // Actualización optimista local
    this.notificaciones.update((list) =>
      list.map((n) => (n.id === id ? { ...n, leida: true } : n))
    );
    this.notificacionesEquipo.update((list) =>
      list.map((n) => (n.id === id ? { ...n, leida: true } : n))
    );

    this.sailsService.post('/notificaciones/marcar-leida', { id }).subscribe({
      error: () => this.cargar(), // revertir en caso de error
    });
  }

  marcarTodasLeidas(): void {
    this.notificaciones.update((list) => list.map((n) => ({ ...n, leida: true })));
    this.notificacionesEquipo.update((list) => list.map((n) => ({ ...n, leida: true })));

    this.sailsService.post('/notificaciones/marcar-todas-leidas', {}).subscribe({
      error: () => this.cargar(),
    });
  }

  /** Navega a la URL de la notificación y la marca como leída. */
  abrir(notif: Notificacion): void {
    if (!notif.leida) {
      this.marcarLeida(notif.id);
    }
    if (notif.url_destino) {
      this.router.navigateByUrl(notif.url_destino);
    }
  }

  // ── Socket ───────────────────────────────────────────────────────────────────

  private suscribirSocket(): void {
    this.sailsService
      .on('user')
      .subscribe((msg: { tipo?: string; notificacion?: Notificacion }) => {
        if (msg?.tipo === 'NUEVA_NOTIFICACION' && msg.notificacion) {
          const nueva: Notificacion = msg.notificacion;

          // Decidir en qué lista va: si es propia → notificaciones, si no → equipo
          // El backend ya divide: el idUsuario primario en notificaciones, el sub en equipo.
          // En el frontend simplemente añadimos al principio de la lista the correct one.
          this.notificaciones.update((list) => [nueva, ...list].slice(0, 50));
        }
      });
  }
}
